import { Head, Link, router, useForm } from '@inertiajs/react'
import { ArrowLeft, Trash2 } from 'lucide-react'
import type { ReactNode } from 'react'

import { PageHeader } from '@/components/PageHeader'
import { Button } from '@/components/ui/button'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { DashboardLayout } from '@/layouts/DashboardLayout'
import { industriesPath, industryPath } from '@/lib/routes'

import { IndustryFormFields, type IndustryFormData } from './_Form'

type EditIndustry = IndustryFormData & {
  id: number
}

type IndustriesEditProps = {
  industry: EditIndustry
}

function IndustriesEdit({ industry }: IndustriesEditProps) {
  const form = useForm<IndustryFormData>({
    name_en: industry.name_en ?? '',
    name_es: industry.name_es ?? '',
    emoji: industry.emoji ?? '',
  })

  return (
    <>
      <Head title={`${industry.name_en || 'Industry'} — Industries`} />

      <div className="mx-auto max-w-3xl px-8 py-10">
        <Link
          href={industriesPath()}
          className="mb-6 inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to industries
        </Link>

        <PageHeader title={industry.name_en || 'Industry'} />

        <form
          onSubmit={(event) => {
            event.preventDefault()
            form.transform((data) => ({ industry: data }))
            form.patch(industryPath(industry.id))
          }}
        >
          <IndustryFormFields
            data={form.data}
            errors={form.errors as Partial<Record<keyof IndustryFormData, string>>}
            setData={form.setData}
          />

          <div className="mt-6 flex justify-end gap-2">
            <Link href={industriesPath()}>
              <Button type="button" variant="ghost">
                Cancel
              </Button>
            </Link>
            <Button type="submit" disabled={form.processing}>
              {form.processing ? 'Saving…' : 'Save changes'}
            </Button>
          </div>
        </form>

        <Card className="mt-6 border-destructive/40">
          <CardHeader>
            <CardTitle className="text-destructive">Delete industry</CardTitle>
            <CardDescription>
              Only works if no companies are currently tagged with this industry.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button
              variant="destructive"
              onClick={() => {
                if (window.confirm(`Delete ${industry.name_en || 'industry'}?`)) {
                  router.delete(industryPath(industry.id))
                }
              }}
            >
              <Trash2 />
              Delete
            </Button>
          </CardContent>
        </Card>
      </div>
    </>
  )
}

export default Object.assign(IndustriesEdit, {
  layout: (page: ReactNode) => <DashboardLayout>{page}</DashboardLayout>,
})
